// =====================================================
// Swiss Finance OS - Transaction Service
// Database service for transaction operations
// =====================================================

import { supabase, getCurrentUserOrganization } from '@/integrations/supabase/client';
import type { Database, TransactionStatus } from '@/types/database';

type SupabaseTransactionRow = Database['public']['Tables']['transactions']['Row'];

interface ClassificationPayload {
  accountNumber?: string;
  accountName?: string;
  confidence?: number;
  vatRate?: number;
  vatApplicable?: boolean;
  reasoning?: string;
  riskFactors?: unknown;
  requiresReview?: boolean;
  source?: string;
}

interface TransactionEnhancements {
  counterparty_name: string | null;
  debit_account: string | null;
  credit_account: string | null;
  swiss_vat_rate: number | null;
  swiss_vat_amount: number | null;
  vat_applicable: boolean;
  ai_confidence_score: number | null;
  ai_reasoning: string | null;
  classification_status: TransactionStatus;
  classification_source: string | null;
  classification_risk_factors: unknown[];
  classification_payload: ClassificationPayload | null;
  journal_entry_id?: string;
  reconciliation_status?: 'reconciled' | 'partially_reconciled' | 'unreconciled';
  reconciled_amount?: number;
}

export type TransactionRow = SupabaseTransactionRow & TransactionEnhancements;

const BANK_ACCOUNT_NUMBER = '1010';

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const parseClassification = (payload: SupabaseTransactionRow['ai_classification']): ClassificationPayload | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  return payload as ClassificationPayload;
};

const normalizeConfidence = (
  rawConfidence: SupabaseTransactionRow['ai_classification_confidence'],
  classificationConfidence?: number
): number | null => {
  if (isNumber(rawConfidence)) {
    if (rawConfidence <= 1) {
      return Math.max(0, Math.min(100, rawConfidence * 100));
    }
    return Math.max(0, Math.min(100, rawConfidence));
  }

  if (isNumber(classificationConfidence)) {
    return Math.max(0, Math.min(100, classificationConfidence));
  }

  return null;
};

const determineStatus = (confidence: number | null, requiresReview: boolean | undefined): TransactionStatus => {
  if (requiresReview && (confidence ?? 0) < 70) {
    return 'flagged';
  }

  if (confidence === null) {
    return 'pending';
  }

  if (confidence >= 90) {
    return 'approved';
  }

  if (confidence >= 70) {
    return 'reviewed';
  }

  return 'pending';
};

const computeVatAmount = (amount: number, vatRate: number | null, vatApplicable: boolean): number | null => {
  if (!vatApplicable || !isNumber(vatRate)) {
    return null;
  }

  const vat = Math.abs(amount) * (vatRate / 100);
  return Math.round(vat * 100) / 100; // round to 2 decimals
};

export const transformTransactionRow = (row: SupabaseTransactionRow): TransactionRow => {
  const classification = parseClassification(row.ai_classification);
  const classificationConfidence = normalizeConfidence(row.ai_classification_confidence, classification?.confidence);
  const vatRate = classification?.vatRate ?? null;
  const vatApplicable = classification?.vatApplicable ?? (isNumber(vatRate) ? vatRate > 0 : false);
  const vatAmount = computeVatAmount(row.amount, vatRate, vatApplicable);

  const suggestedAccount = classification?.accountNumber ?? row.account_number ?? null;
  const normalizedAccount = suggestedAccount ?? 'Uncategorized';
  const isExpense = row.amount < 0;
  const debitAccount = isExpense ? normalizedAccount : BANK_ACCOUNT_NUMBER;
  const creditAccount = isExpense ? BANK_ACCOUNT_NUMBER : normalizedAccount;
  const classificationStatus = determineStatus(classificationConfidence, classification?.requiresReview);
  const riskFactorsRaw = classification?.riskFactors;
  const riskFactors = Array.isArray(riskFactorsRaw) ? riskFactorsRaw : [];

  return {
    ...row,
    counterparty_name: row.counterparty ?? null,
    debit_account: debitAccount,
    credit_account: creditAccount,
    swiss_vat_rate: vatRate,
    swiss_vat_amount: vatAmount,
    vat_applicable: vatApplicable,
    ai_confidence_score: classificationConfidence,
    ai_reasoning: classification?.reasoning ?? null,
    classification_status: classificationStatus,
    classification_source: classification?.source ?? null,
    classification_risk_factors: riskFactors,
    classification_payload: classification,
  };
};

export interface TransactionFilters {
  mandateId?: string;
  startDate?: string;
  endDate?: string;
  status?: TransactionStatus[];
  minAmount?: number;
  maxAmount?: number;
  confidenceThreshold?: number;
  minConfidence?: number;
  maxConfidence?: number;
  counterparty?: string;
  account?: string;
  search?: string;
}

export interface TransactionListResponse {
  transactions: TransactionRow[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface DashboardMetrics {
  totalTransactionsToday: number;
  totalTransactionsThisMonth: number;
  monthlyRevenue: number;
  monthlyExpenses: number;
  pendingReviews: number;
  flaggedTransactions: number;
  automationRate: number;
  averageConfidenceScore: number;
  processingEfficiency: number;
  activeMandates: number;
}

class TransactionService {
  /**
   * Get paginated transactions for current user's organization
   */
  async getTransactions(
    page: number = 1,
    pageSize: number = 20,
    filters: TransactionFilters = {}
  ): Promise<TransactionListResponse> {
    const userOrg = await getCurrentUserOrganization();
    if (!userOrg) {
      throw new Error('User organization not found');
    }

    const organizationId = userOrg.company_id;
    if (!organizationId) {
      throw new Error('Organization context missing for current user');
    }

    let query = supabase
      .from('transactions')
      .select('*')
      .eq('organization_id', organizationId)
      .order('transaction_date', { ascending: false })
      .limit(5000); // Hard limit to prevent egress explosion

    // Apply filters
    if (filters.mandateId) {
      query = query.eq('mandate_id', filters.mandateId);
    }

    if (filters.startDate) {
      query = query.gte('transaction_date', filters.startDate);
    }

    if (filters.endDate) {
      query = query.lte('transaction_date', filters.endDate);
    }

    if (filters.status && filters.status.length > 0) {
      // Applied after data transformation
    }

    if (filters.minAmount !== undefined) {
      query = query.gte('amount', filters.minAmount);
    }

    if (filters.maxAmount !== undefined) {
      query = query.lte('amount', filters.maxAmount);
    }

    if (filters.confidenceThreshold !== undefined) {
      // Applied after data transformation
    }

    if (filters.counterparty) {
      query = query.ilike('counterparty', `%${filters.counterparty}%`);
    }

    if (filters.account) {
      query = query.eq('account_number', filters.account);
    }

    if (filters.search) {
      query = query.or(`description.ilike.%${filters.search}%,counterparty.ilike.%${filters.search}%,reference.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching transactions:', error);
      throw new Error(`Failed to fetch transactions: ${error.message}`);
    }

    const transformed = (data ?? []).map(transformTransactionRow);

    const filtered = transformed.filter((transaction) => {
      const matchesStatus = !filters.status || filters.status.length === 0
        ? true
        : filters.status.includes(transaction.classification_status);

      let matchesConfidence = true;
      if (filters.minConfidence !== undefined && filters.maxConfidence !== undefined) {
        const score = transaction.ai_confidence_score ?? 0;
        matchesConfidence = score >= filters.minConfidence && score <= filters.maxConfidence;
      } else if (filters.confidenceThreshold !== undefined) {
        matchesConfidence = (transaction.ai_confidence_score ?? 0) >= filters.confidenceThreshold;
      }

      return matchesStatus && matchesConfidence;
    });

    const totalCount = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize || 1));
    const safeCurrentPage = Math.min(page, totalPages);
    const startIndex = (safeCurrentPage - 1) * pageSize;
    const paginated = filtered.slice(startIndex, startIndex + pageSize);

    return {
      transactions: paginated,
      totalCount,
      totalPages,
      currentPage: safeCurrentPage,
      hasNextPage: safeCurrentPage < totalPages,
      hasPreviousPage: safeCurrentPage > 1,
    };
  }

  /**
   * Get dashboard metrics for current organization
   */
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const userOrg = await getCurrentUserOrganization();
    if (!userOrg) {
      throw new Error('User organization not found');
    }

    const organizationId = userOrg.company_id;
    if (!organizationId) {
      throw new Error('Organization context missing for current user');
    }

    const today = new Date().toISOString().split('T')[0];
    const monthStart = new Date();
    monthStart.setDate(1);
    const monthStartString = monthStart.toISOString().split('T')[0];

    try {
      // Execute all queries in parallel for better performance
      const [
        { data: todayTransactions },
        { data: monthTransactions },
        { count: activeMandatesCount }
      ] = await Promise.all([
        // Get today's transactions (limited for performance)
        supabase
          .from('transactions')
          .select('*')
          .eq('organization_id', organizationId)
          .gte('transaction_date', today)
          .limit(1000),
        
        // Get this month's transactions (limited for performance)
        supabase
          .from('transactions')
          .select('*')
          .eq('organization_id', organizationId)
          .gte('transaction_date', monthStartString)
          .limit(2000),
        
        // Get active mandates count
        supabase
          .from('mandates')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .eq('status', 'active')
      ]);

      const todayTxns = (todayTransactions ?? []).map(transformTransactionRow);
      const monthTxns = (monthTransactions ?? []).map(transformTransactionRow);

      // Calculate metrics
      const totalTransactionsToday = todayTxns.length;
      const totalTransactionsThisMonth = monthTxns.length;

      const monthlyRevenue = monthTxns
        .filter(t => t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0);

      const monthlyExpenses = Math.abs(monthTxns
        .filter(t => t.amount < 0)
        .reduce((sum, t) => sum + t.amount, 0));

      const pendingReviews = monthTxns
        .filter(t => t.classification_status === 'pending').length;

      const flaggedTransactions = monthTxns
        .filter(t => t.classification_status === 'flagged').length;

      // Calculate automation rate (percentage of high confidence transactions)
      const highConfidenceTransactions = monthTxns
        .filter(t => (t.ai_confidence_score || 0) >= 90).length;
      const automationRate = totalTransactionsThisMonth > 0
        ? (highConfidenceTransactions / totalTransactionsThisMonth) * 100
        : 0;

      // Calculate average confidence score
      const confidenceScores = monthTxns
        .map(t => t.ai_confidence_score)
        .filter((score): score is number => typeof score === 'number');
      const averageConfidenceScore = confidenceScores.length > 0
        ? confidenceScores.reduce((sum, score) => sum + score, 0) / confidenceScores.length
        : 0;

      // Processing efficiency (approved vs total)
      const approvedTransactions = monthTxns
        .filter(t => t.classification_status === 'approved').length;
      const processingEfficiency = totalTransactionsThisMonth > 0
        ? (approvedTransactions / totalTransactionsThisMonth) * 100
        : 0;

      return {
        totalTransactionsToday,
        totalTransactionsThisMonth,
        monthlyRevenue,
        monthlyExpenses,
        pendingReviews,
        flaggedTransactions,
        automationRate,
        averageConfidenceScore,
        processingEfficiency,
        activeMandates: activeMandatesCount || 0,
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw new Error(`Failed to fetch dashboard metrics: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update transaction classification
   */
  async updateTransactionClassification(
    transactionId: string,
    updates: {
      account_number?: string;
      human_verified?: boolean;
      needs_review?: boolean;
    }
  ): Promise<TransactionRow> {
    const userOrg = await getCurrentUserOrganization();
    if (!userOrg) {
      throw new Error('User organization not found');
    }

    const organizationId = userOrg.company_id;
    if (!organizationId) {
      throw new Error('Organization context missing for current user');
    }

    // Get original transaction data before update
    const { data: originalData } = await supabase
      .from('transactions')
      .select('*')
      .eq('id', transactionId)
      .maybeSingle();

    const { data, error } = await supabase
      .from('transactions')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', transactionId)
      .eq('organization_id', organizationId)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error updating transaction:', error);
      throw new Error(`Failed to update transaction: ${error.message}`);
    }

    if (!data) {
      throw new Error('Transaction not found or access denied');
    }

    // Check if this is a manual correction worthy of learning
    if (originalData && updates.account_number && updates.human_verified) {
      const originalConfidence = originalData.ai_classification_confidence || 0;
      const accountChanged = originalData.account_number !== updates.account_number;
      
      // Create learning rule if confidence was low and account was corrected
      if (accountChanged && originalConfidence < 80) {
        try {
          await this.createLearningRuleFromCorrection(data, organizationId);
        } catch (ruleError) {
          console.error('Failed to create learning rule:', ruleError);
          // Don't fail the main update if rule creation fails
        }
      }
    }

    return transformTransactionRow(data);
  }

  /**
   * Extract keywords from description for pattern learning
   */
  private extractKeywords(description: string): string[] {
    const stopWords = ['the', 'and', 'for', 'with', 'from', 'der', 'die', 'das', 'von', 'zur', 'bei'];
    
    return description
      .toLowerCase()
      .replace(/[^a-zäöüß\s]/gi, '') // Remove special chars but keep German umlauts
      .split(/\s+/)
      .filter(word => word.length > 3 && !stopWords.includes(word))
      .slice(0, 5); // Top 5 keywords
  }

  /**
   * Create a learning rule from a manual correction
   */
  private async createLearningRuleFromCorrection(
    transaction: any,
    organizationId: string
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Extract pattern from transaction
    const counterparty = transaction.counterparty || '';
    const description = transaction.description || '';
    const account = transaction.account_number || '';
    const vatRate = transaction.vat_rate || 8.1;
    
    // Determine pattern type based on available data
    const patternType = counterparty ? 'counterparty' : 'description_pattern';
    const patternKey = counterparty || description.toLowerCase();
    
    if (!patternKey || !account) return;

    // Check if similar rule already exists
    const { data: existingRules } = await supabase
      .from('learned_rules')
      .select('id, usage_count, success_count, current_confidence')
      .eq('organization_id', organizationId)
      .eq('pattern_type', patternType)
      .eq('pattern_key', patternKey.toLowerCase())
      .limit(1);

    if (existingRules && existingRules.length > 0) {
      // Update existing rule: increment frequency and boost confidence
      const existing = existingRules[0];
      const newUsageCount = (existing.usage_count || 1) + 1;
      const newSuccessCount = (existing.success_count || 1) + 1;
      const newConfidence = Math.min(existing.current_confidence + 2, 99);

      await supabase
        .from('learned_rules')
        .update({
          usage_count: newUsageCount,
          success_count: newSuccessCount,
          current_confidence: newConfidence,
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id);

      console.log(`Updated existing rule: ${patternKey} (confidence: ${newConfidence}%)`);
      return;
    }

    // Create new learning rule
    const { error: ruleError } = await supabase
      .from('learned_rules')
      .insert({
        user_id: user.id,
        organization_id: organizationId,
        pattern_type: patternType,
        pattern_key: patternKey.toLowerCase(),
        pattern_value: description,
        suggested_debit_account: account,
        suggested_credit_account: '1020', // Default PostFinance account
        base_confidence: 0.80,
        current_confidence: 0.80,
        is_active: true,
        scope: 'organization',
        is_shared: true,
        usage_count: 1,
        success_count: 1,
      });

    if (ruleError) {
      throw ruleError;
    }

    console.log(`✅ Learning rule created from correction: ${patternType} -> ${account}`);

    // Also create keyword-based patterns for better generalization
    const keywords = this.extractKeywords(description);
    for (const keyword of keywords) {
      // Check if keyword pattern already exists
      const { data: keywordRules } = await (supabase as any)
        .from('learned_rules')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('pattern_type', 'keyword')
        .eq('pattern_key', keyword)
        .limit(1);

      if (!keywordRules || keywordRules.length === 0) {
        // Create new keyword pattern (lower confidence)
        await supabase
          .from('learned_rules')
          .insert({
            user_id: user.id,
            organization_id: organizationId,
            pattern_type: 'keyword',
            pattern_key: keyword,
            pattern_value: description,
            suggested_debit_account: account,
            suggested_credit_account: '1020',
            base_confidence: 0.70, // Lower confidence for keywords
            current_confidence: 0.70,
            vat_code: vatRate.toString(),
            is_active: true,
            scope: 'organization',
            is_shared: true,
            usage_count: 1,
            success_count: 1,
          });

        console.log(`✅ Keyword pattern created: "${keyword}" -> ${account}`);
      }
    }
  }

  /**
   * Bulk approve transactions
   */
  async bulkApproveTransactions(transactionIds: string[]): Promise<void> {
    const userOrg = await getCurrentUserOrganization();
    if (!userOrg) {
      throw new Error('User organization not found');
    }

    const organizationId = userOrg.company_id;
    if (!organizationId) {
      throw new Error('Organization context missing for current user');
    }

    const { error } = await supabase
      .from('transactions')
      .update({
        human_verified: true,
        needs_review: false,
        updated_at: new Date().toISOString(),
      })
      .in('id', transactionIds)
      .eq('organization_id', organizationId);

    if (error) {
      console.error('Error bulk approving transactions:', error);
      throw new Error(`Failed to bulk approve transactions: ${error.message}`);
    }
  }

  /**
   * Get monthly revenue data for charts
   */
  async getMonthlyRevenueData(months: number = 12): Promise<{
    month: string;
    actual: number | null;
    forecast: number | null;
  }[]> {
    const userOrg = await getCurrentUserOrganization();
    if (!userOrg) {
      throw new Error('User organization not found');
    }

    const organizationId = userOrg.company_id;
    if (!organizationId) {
      throw new Error('Organization context missing for current user');
    }

    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - months);

    const { data, error } = await supabase
      .from('transactions')
      .select('transaction_date, amount')
      .eq('organization_id', organizationId)
      .gte('transaction_date', startDate.toISOString().split('T')[0])
      .gt('amount', 0); // Only revenue transactions

    if (error) {
      console.error('Error fetching revenue data:', error);
      throw new Error(`Failed to fetch revenue data: ${error.message}`);
    }

    // Group by month
    const monthlyData = new Map<string, number>();

    (data || []).forEach(transaction => {
      const date = new Date(transaction.transaction_date);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
      const current = monthlyData.get(monthKey) || 0;
      monthlyData.set(monthKey, current + transaction.amount);
    });

    // Generate complete months array
    const result = [];
    const currentDate = new Date();

    for (let i = months - 1; i >= 0; i--) {
      const date = new Date();
      date.setMonth(currentDate.getMonth() - i);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short' });

      const actual = monthlyData.get(monthKey) || null;
      const forecast = i < 0 ? null : (actual ? actual * 1.1 : null); // Simple 10% growth forecast

      result.push({
        month: monthKey,
        actual,
        forecast,
      });
    }

    return result;
  }

  /**
   * Get mandate list for current organization
   */
  async getMandates(): Promise<{
    id: string;
    client_name: string;
    status: string;
    mandate_type: string;
    pending_transactions: number;
    flagged_transactions: number;
  }[]> {
    const userOrg = await getCurrentUserOrganization();
    if (!userOrg) {
      throw new Error('User organization not found');
    }

    const organizationId = userOrg.company_id;
    if (!organizationId) {
      throw new Error('Organization context missing for current user');
    }

    const { data: mandates, error: mandatesError } = await supabase
      .from('mandates')
      .select('id, client_name, status, mandate_type')
      .eq('organization_id', organizationId)
      .eq('status', 'active');

    if (mandatesError) {
      console.error('Error fetching mandates:', mandatesError);
      throw new Error(`Failed to fetch mandates: ${mandatesError.message}`);
    }

    // For each mandate, get transaction counts
    const mandatesWithCounts = await Promise.all(
      (mandates || []).map(async (mandate) => {
        const { data: transactionCounts } = await supabase
          .from('transactions')
          .select('id, amount, ai_classification_confidence, ai_classification, account_number')
          .eq('mandate_id', mandate.id)
          .eq('organization_id', organizationId);

        const transformedTransactions = (transactionCounts ?? []).map((row: any) => transformTransactionRow(row));

        const pending_transactions = transformedTransactions
          .filter(t => t.classification_status === 'pending').length;

        const flagged_transactions = transformedTransactions
          .filter(t => t.classification_status === 'flagged').length;

        return {
          ...mandate,
          pending_transactions,
          flagged_transactions,
        };
      })
    );

    return mandatesWithCounts;
  }

  /**
   * Link a transaction with a booking (journal entry)
   */
  async linkToBooking(transactionId: string, journalEntryId: string): Promise<TransactionRow | null> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .update({
          journal_entry_id: journalEntryId,
          classification_status: 'approved' as TransactionStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', transactionId)
        .select()
        .single();

      if (error) {
        console.error('Error linking transaction to booking:', error);
        throw error;
      }

      return data ? transformTransactionRow(data) : null;
    } catch (error) {
      console.error('Failed to link transaction to booking:', error);
      throw error;
    }
  }

  /**
   * Unlink a transaction from a booking
   */
  async unlinkFromBooking(transactionId: string): Promise<TransactionRow | null> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .update({
          journal_entry_id: null,
          classification_status: 'pending' as TransactionStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', transactionId)
        .select()
        .single();

      if (error) {
        console.error('Error unlinking transaction from booking:', error);
        throw error;
      }

      return data ? transformTransactionRow(data) : null;
    } catch (error) {
      console.error('Failed to unlink transaction from booking:', error);
      throw error;
    }
  }

  /**
   * Get transactions linked to a specific booking
   */
  async getTransactionsByBooking(journalEntryId: string): Promise<TransactionRow[]> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('journal_entry_id', journalEntryId)
        .order('transaction_date', { ascending: false });

      if (error) {
        console.error('Error fetching transactions by booking:', error);
        throw error;
      }

      return data ? data.map(transformTransactionRow) : [];
    } catch (error) {
      console.error('Failed to get transactions by booking:', error);
      throw error;
    }
  }

  /**
   * Update transaction reconciliation status
   */
  async updateReconciliationStatus(
    transactionId: string,
    status: 'reconciled' | 'partially_reconciled' | 'unreconciled',
    reconciledAmount?: number
  ): Promise<TransactionRow | null> {
    try {
      const updateData: any = {
        reconciliation_status: status,
        updated_at: new Date().toISOString(),
      };

      if (reconciledAmount !== undefined) {
        updateData.reconciled_amount = reconciledAmount;
      }

      const { data, error } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('id', transactionId)
        .select()
        .single();

      if (error) {
        console.error('Error updating reconciliation status:', error);
        throw error;
      }

      return data ? transformTransactionRow(data) : null;
    } catch (error) {
      console.error('Failed to update reconciliation status:', error);
      throw error;
    }
  }
}

export const transactionService = new TransactionService();
